import { NavLink } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import {
  Archive,
  BookOpen,
  ChevronRight,
  Globe,
  Globe2,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Newspaper,
  Package,
  PlugZap,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  SearchCheck,
  Server,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { IconButton, Logo, ThemeSwitch } from '@/components/ui';
import { useCurrentUser, useRoles, useSignOut } from '@/hooks/useAuth';
import { BRAND } from '@/lib/brand';
import { usePreferences } from '@/store/preferencesStore';
import { initial } from '@/utils/format';
import { roleList } from '@/utils/labels';
import { can } from '@/utils/permissions';

interface NavEntry {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
  /** Hidden when the account cannot use the screen. The server checks again. */
  readonly permission?: 'team.manage' | 'templates.read' | 'audit.read' | 'domains.purchase' | 'content.manage' | 'secrets.manage';
}

const NAV: readonly NavEntry[] = [
  { to: '/', label: 'Обзор', icon: LayoutDashboard },
  { to: '/sites', label: 'Сайты', icon: Globe },
  { to: '/seo', label: 'SEO', icon: SearchCheck },
  { to: '/content', label: 'Контент', icon: Newspaper, permission: 'content.manage' },
  { to: '/servers', label: 'Серверы', icon: Server },
  { to: '/backups', label: 'Резервные копии', icon: Archive },
  { to: '/domains', label: 'Домены', icon: Globe2, permission: 'domains.purchase' },
  { to: '/jobs', label: 'Задачи', icon: ListChecks },
  { to: '/templates', label: 'Шаблоны', icon: Package, permission: 'templates.read' },
  { to: '/integrations', label: 'Интеграции', icon: PlugZap, permission: 'secrets.manage' },
  { to: '/team', label: 'Сотрудники', icon: Users, permission: 'team.manage' },
  { to: '/audit', label: 'Журнал', icon: ScrollText, permission: 'audit.read' },
  { to: '/docs', label: 'Справка', icon: BookOpen },
];

export function Sidebar({
  onOpenAccount,
  onSignedOut,
}: {
  readonly onOpenAccount: () => void;
  readonly onSignedOut: () => void;
}): ReactNode {
  const roles = useRoles();
  const user = useCurrentUser().data;
  // Settled, not success: if the call fails because the session was already
  // gone, staying on a dashboard the server will refuse to serve is the worse
  // outcome of the two.
  const logout = useSignOut({ onSettled: onSignedOut });

  const entries = NAV.filter((entry) => !entry.permission || can(roles, entry.permission));
  const name = user ? (user.displayName ?? user.login) : '';
  const collapsed = usePreferences((state) => state.sidebarCollapsed);
  const toggle = usePreferences((state) => state.toggleSidebar);

  // Folded, a section is only its icon: its name comes up beside it.
  const [tip, setTip] = useState<{ text: string; left: number; top: number } | null>(null);
  const showTip = (text: string, element: HTMLElement): void => {
    const box = element.getBoundingClientRect();
    setTip({ text, left: box.right + 8, top: box.top + box.height / 2 });
  };
  const tipFor = (text: string) =>
    collapsed
      ? {
          onMouseEnter: (event: React.MouseEvent<HTMLElement>) => showTip(text, event.currentTarget),
          onFocus: (event: React.FocusEvent<HTMLElement>) => showTip(text, event.currentTarget),
          onMouseLeave: () => setTip(null),
          onBlur: () => setTip(null),
        }
      : {};

  return (
    <aside className="sidebar">
      {/* Both logos are drawn; the stylesheet shows the mark alone when the
          sidebar is folded, and the whole logo on a phone. */}
      <div className="brand">
        <Logo className="brand-logo" height={28} />
        <Logo className="brand-mark" variant="mark" height={28} />
        <div className="brand-sub">{BRAND.tagline}</div>
      </div>

      <nav className="nav" aria-label="Разделы">
        <div className="nav-label">Разделы</div>
        {entries.map(({ to, label: text, icon: Icon }) => (
          <NavLink key={to} to={to} end={to === '/'} {...(collapsed ? { 'aria-label': text } : {})} {...tipFor(text)}>
            <Icon size={16} aria-hidden="true" />
            <span className="nav-text">{text}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-foot">
        {/* The user block is the way into account settings, so it is a button
            rather than a label with a menu hidden somewhere else. */}
        <button type="button" className="who" onClick={onOpenAccount} {...(collapsed ? { 'aria-label': name } : {})} {...tipFor(name)}>
          <span className="avatar" aria-hidden="true">
            {initial(name)}
          </span>
          <span className="who-text">
            <span className="who-name">{name}</span>
            <span className="who-role">{roleList(roles)}</span>
          </span>
          <ChevronRight size={14} aria-hidden="true" className="faint who-chevron" />
        </button>

        <div className="row-between">
          <ThemeSwitch />
          <div className="sidebar-buttons">
            <IconButton
              className="sidebar-toggle"
              icon={collapsed ? PanelLeftOpen : PanelLeftClose}
              label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
              variant="ghost"
              aria-expanded={!collapsed}
              onClick={() => {
                setTip(null);
                toggle();
              }}
            />
            <IconButton
              icon={LogOut}
              label="Выйти"
              variant="ghost"
              busy={logout.isPending}
              onClick={() => logout.mutate()}
            />
          </div>
        </div>
      </div>

      {tip ? (
        <div className="nav-tip" role="tooltip" style={{ left: tip.left, top: tip.top }}>
          {tip.text}
        </div>
      ) : null}
    </aside>
  );
}
